import type { IconProps } from '@hedepy/icons/Icon'
import { cva, type VariantProps } from 'class-variance-authority'
import { cloneElement, type ReactElement, type ReactNode } from 'react'

import { cn } from '@/legacy-src/lib/utils'

import { Text, type TextProps } from '@/src/domains/design-system/components/Text'

const alertVariants = cva('flex flex-col gap-2 rounded-xl p-3 text-petrol-500', {
  variants: {
    variant: {
      yellow: 'bg-yellow-500',
      blue: 'bg-blue-500',
      sand: 'bg-sand-400',
      'no-background': 'px-0',
    },
  },
})

interface AlertVariantProps extends VariantProps<typeof alertVariants> {}

export interface AlertProps {
  variant: NonNullable<AlertVariantProps['variant']>
  icon: ReactElement<Pick<IconProps, 'size' | 'className' | 'aria-hidden'>>
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  className?: string
}

const titleWeightMap: Record<AlertProps['variant'], TextProps['weight']> = {
  blue: 'bold',
  yellow: 'regular',
  sand: 'regular',
  'no-background': 'regular',
}

export const Alert = ({ variant, icon, title, description, action, className }: AlertProps) => {
  return (
    <div className={alertVariants({ variant, className })}>
      <div className="flex items-center gap-2" role="alert">
        {cloneElement(icon, {
          size: 24,
          'aria-hidden': true,
          className: cn('shrink-0 self-start', icon.props.className),
        })}
        <div className="flex flex-1 flex-col">
          <Text element="div" variant="sm" weight={titleWeightMap[variant]} className="text-inherit m-0">
            {title}
          </Text>
          {description ? (
            <Text element="div" variant="sm" weight="regular" className="text-inherit m-0">
              {description}
            </Text>
          ) : null}
        </div>
      </div>
      {action}
    </div>
  )
}
